import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMaterialDetailModal } from '../../../../contexts/MaterialDetailModalContext.tsx';
import { isAxiosError } from 'axios';
import { getCommerceMaterials } from '../../../../services/commerceService.ts';
import type { UserCommerceRef } from '../../../../utils/userCommerces.ts';
import CommerceMaterialsCatalog from '../MisMaterialesComercio/CommerceMaterialsCatalog.tsx';
import {
  type FlatMaterialRow,
  extractCommerceMaterialsDataArray,
  flattenCategories,
  parseCommerceMaterialsPayload,
} from '../MisMaterialesComercio/commerceMaterialsModel.ts';
import styles from './ProfileCommerceMaterialsPublic.module.scss';

interface ProfileCommerceMaterialsPublicProps {
  commerces: UserCommerceRef[];
}

const commerceListIncludesId = (list: UserCommerceRef[], id: number): boolean =>
  list.some((c) => Number(c.id) === Number(id));

const ProfileCommerceMaterialsPublic: React.FC<ProfileCommerceMaterialsPublicProps> = ({
  commerces,
}) => {
  const { openMaterialDetail } = useMaterialDetailModal();
  const [searchParams] = useSearchParams();
  const [commerceId, setCommerceId] = useState<number | null>(() => commerces[0]?.id ?? null);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);
  const [flatRows, setFlatRows] = useState<FlatMaterialRow[]>([]);

  useEffect(() => {
    if (commerces.length === 0) return;
    const raw = searchParams.get('commerceId');
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0 && commerceListIncludesId(commerces, n)) {
        setCommerceId(n);
        return;
      }
    }
    setCommerceId((prev) => {
      if (prev != null && commerceListIncludesId(commerces, prev)) return prev;
      return commerces[0]?.id ?? null;
    });
  }, [commerces, searchParams]);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    setBlocked(false);
    setWarn(null);
    try {
      const res = await getCommerceMaterials(id);
      const raw = extractCommerceMaterialsDataArray(res.data);
      const categories = parseCommerceMaterialsPayload(raw, id);
      setFlatRows(flattenCategories(categories));
    } catch (e: unknown) {
      const status = isAxiosError(e) ? e.response?.status : null;
      if (status === 401 || status === 403) {
        setBlocked(true);
      } else {
        setWarn('No pudimos cargar el catálogo en este momento.');
      }
      setFlatRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (commerceId == null) return;
    void load(commerceId);
  }, [commerceId, load]);

  if (commerces.length === 0) return null;

  const singleName = commerces.length === 1 ? commerces[0]?.name?.trim() : "";
  const sectionIntro =
    commerces.length === 1
      ? "Listado de referencia en este perfil; precios y condiciones, consultá directamente al comercio."
      : "Elegí un local para ver su listado; precios y condiciones por consulta.";

  return (
    <div className={styles.wrap}>
      <h4 className={styles.title}>Materiales</h4>
      {singleName ? <p className={styles.commerceLead}>{singleName}</p> : null}
      <p className={styles.intro}>{sectionIntro}</p>
      {commerces.length > 1 ? (
        <div className={styles.field}>
          <label className={styles.label} htmlFor="pub-commerce-materials">
            Local
          </label>
          <select
            id="pub-commerce-materials"
            className={styles.select}
            value={commerceId ?? ''}
            onChange={(e) => setCommerceId(Number(e.target.value))}
            disabled={loading}
          >
            {commerces.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {loading ? <p className={styles.meta}>Cargando catálogo…</p> : null}
      {blocked && !loading ? (
        <p className={styles.notice}>
          El catálogo no está disponible de forma anónima. Iniciá sesión o contactá al comercio para cotizar.
        </p>
      ) : null}
      {warn && !loading && !blocked ? <p className={styles.notice}>{warn}</p> : null}
      {!loading && !blocked ? (
        <CommerceMaterialsCatalog
          mode="public"
          flatRows={flatRows}
          onPublicMaterialDetailClick={(id) => void openMaterialDetail(id)}
        />
      ) : null}
    </div>
  );
};

export default ProfileCommerceMaterialsPublic;
